import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const institutionalLinks = [
  { label: 'Sobre nós', href: '#sobre' },
  { label: 'Nossa história', href: '#sobre' },
];

const serviceLinks = [
  { label: 'Produtos', href: '/produtos' },
  { label: 'Licitações', href: '#licitacoes' },
  { label: 'Consulta de Atas', href: '/atas' },
];

const legalLinks = [
  { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
  { label: 'Termos de Uso', href: '/termos-de-uso' },
];

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;

    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d95uq4l7gtqne2as9nu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || 'Erro ao enviar mensagem. Tente novamente.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Erro de conexão. Tente novamente mais tarde.');
    }
  };

  return (
    <footer id="contato" className="bg-donnos-dark text-white">
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 py-10 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-10 lg:gap-8">
          <div className="lg:col-span-5">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">Fale conosco</h3>
            <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6">
              Preencha o formulário abaixo e nossa equipe entrará em contato em breve.
            </p>

            <form data-readdy-form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Nome</label>
                  <input
                    type="text"
                    name="nome"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green transition-colors"
                    placeholder="Seu nome"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green transition-colors"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Assunto</label>
                  <input
                    type="text"
                    name="assunto"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green transition-colors"
                    placeholder="Assunto"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Telefone</label>
                  <input
                    type="tel"
                    name="telefone"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green transition-colors"
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Mensagem</label>
                <textarea
                  name="mensagem"
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green transition-colors resize-none"
                  placeholder="Sua mensagem..."
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-donnos-green hover:bg-donnos-green-dark disabled:opacity-50 text-white font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-md transition-colors"
              >
                {formStatus === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar mensagem
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>

              {formStatus === 'success' && (
                <div className="flex items-center gap-2 text-donnos-green text-sm">
                  <i className="ri-checkbox-circle-line"></i>
                  Mensagem enviada com sucesso!
                </div>
              )}
              {formStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </div>
              )}
            </form>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs sm:text-sm font-semibold text-donnos-green uppercase tracking-wider mb-3 sm:mb-4">Institucional</h4>
            <ul className="space-y-2 sm:space-y-3">
              {institutionalLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} onClick={(e) => handleLinkClick(e, link.href)} className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <h4 className="text-xs sm:text-sm font-semibold text-donnos-green uppercase tracking-wider mb-3 sm:mb-4 mt-6 sm:mt-8">Serviços</h4>
            <ul className="space-y-2 sm:space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} onClick={(e) => handleLinkClick(e, link.href)} className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-xs sm:text-sm font-semibold text-donnos-green uppercase tracking-wider mb-3 sm:mb-4">Localização</h4>
            <address className="not-italic text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6 leading-relaxed">
              ST SHCS CRS Quadra 502 Bloco C - SN<br />
              Bloco C Loja 37 Parte 3173<br />
              Asa Sul, Brasília - DF<br />
              CEP: 70330-530
            </address>

            <h4 className="text-xs sm:text-sm font-semibold text-donnos-green uppercase tracking-wider mb-3 sm:mb-4">Contato</h4>
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              <a href="tel:+5511988730909" className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                <i className="ri-phone-line text-donnos-green"></i>
                11 9 8873-0909 - Suporte
              </a>
              <a href="tel:+551145510810" className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                <i className="ri-phone-line text-donnos-green"></i>
                11 4551-0810 - Comercial
              </a>
              <a href="https://wa.me/5511988730909" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                <i className="ri-whatsapp-line text-donnos-green"></i>
                WhatsApp Suporte
              </a>
              <a href="mailto:contato@donnosvendas.com" className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                <i className="ri-mail-line text-donnos-green"></i>
                contato@donnosvendas.com
              </a>
              <a href="mailto:licitacao@donnosvendas.com" className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 hover:text-white transition-colors">
                <i className="ri-mail-line text-donnos-green"></i>
                licitacao@donnosvendas.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <a href="#inicio" className="flex h-16 w-[118px] overflow-hidden rounded bg-black/40">
              <img
                src="/assets/donnos-logo.png"
                alt="Donnos - Vendas e Serviços Integrados"
                className="h-full w-full object-contain"
              />
            </a>

            <div className="text-center">
              <p className="text-[10px] sm:text-xs text-gray-500">
                &copy; {new Date().getFullYear()} Donnos. Todos os direitos reservados.
              </p>
              <p className="mt-1 text-[10px] sm:text-xs text-gray-500">
                Orgulhosamente desenvolvido por{' '}
                <a
                  href="https://www.serraconsultoria.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-donnos-green hover:text-white transition-colors"
                >
                  Serra Consultoria
                </a>
              </p>
            </div>

            <div className="flex gap-3 sm:gap-4">
              {legalLinks.map((link) => (
                <a key={link.label} href={link.href} onClick={(e) => handleLinkClick(e, link.href)} className="text-[10px] sm:text-xs text-gray-500 hover:text-white transition-colors">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
